import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { LogService } from '@services/log.service';
import { TankService } from '@services/tank.service';
import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../app.state';
import { selectLastSelectedTankQuickFilter } from '@state/selectors/user-profile.selectors';
import { TankFilterService } from '@services/tank-filter.service';

export const enum TankQuickFilterType {
  ActiveTanks,
  NoRecentUpdates,
  RecentInstalls,
  CriticalLowAlarms,
  LowAlarms,
  LowBattery,
  ReorderAlarms,
  SafetyStockAlarms,
  LeastEfficientDeliveries,
  NeedsDelivery,
  ReceivingLocations,
}
export class TankQuickFilterModel {
  filterType: TankQuickFilterType;
  name: string;
  //value$: Observable<string | number | null>;
  value: string | number | null;
  icon: string;
  color: string;
}

export const ActiveFilter: TankQuickFilterModel = {
  filterType: TankQuickFilterType.ActiveTanks,
  name: 'TankFilters.ActiveTanks',
  value: 0,
  icon: 'active-tanks.svg',
  color: '#00a3e0',
};

export const scrollBy: number = 300;

@Component({
  selector: 'app-tank-quick-filters',
  templateUrl: './tank-quick-filters.component.html',
  styleUrls: ['./tank-quick-filters.component.css'],
})
export class TankQuickFiltersComponent implements AfterViewInit, OnDestroy {
  @ViewChild('scrollableContainer') scrollableContainer: ElementRef<HTMLElement>;

  //used to unsubscribe from observables
  destroy$: Subject<boolean> = new Subject<boolean>();

  /**
   * Each arrow is only useful once there's actually somewhere left to scroll
   * to in that direction - showing the left arrow while already scrolled to
   * the start is the same dead-control problem, just per-direction instead
   * of for the whole row. `ResizeObserver` catches a sidenav toggle or window
   * resize; the filter count itself comes from `tankQuickFilters$` below and
   * can change per-org, which a size observer on this element alone would
   * not catch (its own box doesn't change just because its content grew).
   * The `scroll` listener is what catches scrolling itself flipping either
   * flag - neither a resize nor a content change.
   */
  canScrollLeft = false;
  canScrollRight = false;
  private overflowObserver: ResizeObserver | null = null;

  //Active Tank Filter selected
  currentlySelectedQuickFilter$: Observable<TankQuickFilterModel> = this.store
    .select(selectLastSelectedTankQuickFilter)
    .pipe(takeUntil(this.destroy$));

  tankQuickFilters: TankQuickFilterModel[];
  //Tank Filters - Setup subscription to get the filter data
  // after getting the current Tank Context (CSO and Tank Profile)
  tankQuickFilters$: Observable<TankQuickFilterModel[]> = this.tankFilterService
    .getTankQuickFiltersForFilterBar(this.store)
    .pipe(
      takeUntil(this.destroy$),
      tap((x) => {
        this.tankQuickFilters = x;
        // Deferred: the *ngFor driven by this list hasn't rendered yet on this tick.
        setTimeout(() => this.updateScrollState());
      }),
    );

  constructor(
    private store: Store<AppState>,
    private tankService: TankService,
    private tankFilterService: TankFilterService,
    private logger: LogService,
    private zone: NgZone,
  ) {}

  ngAfterViewInit(): void {
    // Not available in jsdom, so specs measure nothing and the arrows simply stay hidden.
    if (typeof ResizeObserver !== 'undefined') {
      this.zone.runOutsideAngular(() => {
        this.overflowObserver = new ResizeObserver(() => this.updateScrollState());
        this.overflowObserver.observe(this.scrollableContainer.nativeElement);

        this.scrollableContainer.nativeElement.addEventListener('scroll', this.onScroll, {
          passive: true,
        });
      });
    }
    this.updateScrollState();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.overflowObserver?.disconnect();
    this.scrollableContainer?.nativeElement.removeEventListener('scroll', this.onScroll);
  }

  private onScroll = (): void => this.updateScrollState();

  private updateScrollState(): void {
    const el = this.scrollableContainer?.nativeElement;
    // 1px tolerance: sub-pixel layout can leave scrollWidth a fraction past
    // clientWidth even when fully scrolled, which would pin the right arrow on.
    const canScrollLeft = !!el && el.scrollLeft > 1;
    const canScrollRight = !!el && el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    if (canScrollLeft === this.canScrollLeft && canScrollRight === this.canScrollRight) return;

    this.zone.run(() => {
      this.canScrollLeft = canScrollLeft;
      this.canScrollRight = canScrollRight;
    });
  }

  onFilterClicked(filter: TankQuickFilterModel) {
    this.tankFilterService.setLastSelectedTankQuickFilter(
      filter,
      this.currentlySelectedQuickFilter$,
      this.constructor.name,
    );

    return;
  }

  resetFilter() {
    this.tankFilterService.setLastSelectedTankQuickFilter(
      ActiveFilter,
      this.currentlySelectedQuickFilter$,
      this.constructor.name,
    );
  }

  //Horizontal scrolling for filters
  scrollLeft() {
    this.scrollableContainer.nativeElement.scrollLeft -= scrollBy;
  }

  scrollRight() {
    this.scrollableContainer.nativeElement.scrollLeft += scrollBy;
  }
}
